import { useState, useRef, useEffect, useMemo } from 'react'

export interface MultiSelectOption {
  value: string
  label: string
  icon?: string
}

interface Props {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchable?: boolean
  selectAllLabel?: string
  className?: string
  disabled?: boolean
}

export default function MultiSelectDropdown({
  options, selected, onChange, placeholder = 'Select...', searchable = true,
  selectAllLabel = 'Select All', className = '', disabled = false
}: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return options
    const q = search.toLowerCase()
    return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
  }, [options, search])

  const allFilteredSelected = filtered.length > 0 && filtered.every(o => selected.includes(o.value))
  const someSelected = selected.length > 0

  const toggleOption = (val: string) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val])
  }

  const toggleAll = () => {
    if (allFilteredSelected) {
      const filteredVals = new Set(filtered.map(o => o.value))
      onChange(selected.filter(v => !filteredVals.has(v)))
    } else {
      const newSelected = new Set(selected)
      filtered.forEach(o => newSelected.add(o.value))
      onChange(Array.from(newSelected))
    }
  }

  const summary = selected.length === 0
    ? placeholder
    : selected.length === options.length
      ? `All (${options.length})`
      : selected.length <= 2
        ? selected.map(v => options.find(o => o.value === v)?.label || v).join(', ')
        : `${selected.length} selected`

  return (
    <div ref={containerRef} className={`smm-multiselect ${open ? 'smm-multiselect-open' : ''} ${className}`}
      style={{ position: 'relative', display: 'inline-block', minWidth: 180 }}>
      <button type="button" className="smm-multiselect-trigger" disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', border: '1px solid var(--smm-border, #d1d5db)', borderRadius: 6,
          background: disabled ? '#f3f4f6' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '0.9rem', color: selected.length === 0 ? '#9ca3af' : 'inherit',
          minHeight: 36
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ marginLeft: 8, fontSize: '0.7rem', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>&#9660;</span>
      </button>

      {open && (
        <div className="smm-multiselect-panel" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid var(--smm-border, #d1d5db)',
          borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          maxHeight: 280, overflow: 'hidden', display: 'flex', flexDirection: 'column'
        }}>
          {searchable && (
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
              <input type="text" className="smm-input" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search..." autoFocus
                style={{ width: '100%', padding: '4px 8px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: 4 }} />
            </div>
          )}
          <div style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--smm-primary, #4361ee)' }}>
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll}
                style={{ accentColor: 'var(--smm-primary, #4361ee)' }} />
              {selectAllLabel}
            </label>
            {someSelected && (
              <button type="button" onClick={() => onChange([])}
                style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 200, padding: '4px 0' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', color: '#9ca3af', fontSize: '0.85rem' }}>No matches</div>
            )}
            {filtered.map(opt => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px',
                cursor: 'pointer', fontSize: '0.88rem', borderRadius: 3,
                background: selected.includes(opt.value) ? '#f0f5ff' : 'transparent'
              }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f0f5ff')}
                onMouseLeave={e => (e.currentTarget.style.background = selected.includes(opt.value) ? '#f0f5ff' : 'transparent')}>
                <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggleOption(opt.value)}
                  style={{ accentColor: 'var(--smm-primary, #4361ee)' }} />
                {opt.icon && <img src={opt.icon} alt="" style={{ width: 18, height: 18, borderRadius: 3 }} />}
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
